import { ReactNode } from 'react'
import { useStyletron } from 'baseui'
import { SIZE } from 'baseui/button'
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid'
import { Table } from 'baseui/table-semantic'
import { Modal, ModalBody, ModalHeader } from 'baseui/modal'
import { useSelector } from 'react-redux'
import { TFunction } from 'i18next'

import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import IconCarbonCheckmarkOutline from '@/shared/icons/IconCarbonCheckmarkOutline'
import IconCarbonMisuseOutline from '@/shared/icons/IconCarbonMisuseOutline'
import { Card, Header1, Header2 } from '@/shared/theme/styled-components'

import AddEditModal from './AddEditModal'
import useTeamsManagement from './useTeamsManagement'
import { selectTeamsManagementCountrySites } from './TeamsManagementSelectors'
import { CountrySite, Team } from './types'

const formatBusinessVertical = (team: Team, countrySites: CountrySite[], t: TFunction): string => {
  const siteId = team?.site_id
  if (!siteId) return ''
  const site = (countrySites ?? []).find(s => parseInt(String(s.id)) === parseInt(String(siteId)))
  if (!site) return ''
  return t(`teamsManagement:business_verticals.${site.business_type}`, site.business_type)
}

const TeamsManagement = () => {
  const {
    teams,
    selectedTeam,
    teamsListIsLoading,
    teamsError,
    canDelete,
    t,
    isOpen,
    close,
    setIsOpen,
    updateTeam,
    deleteTeam,
  } = useTeamsManagement()

  const countrySites = useSelector(selectTeamsManagementCountrySites)
  const [, theme] = useStyletron()

  const COLUMNS: string[] = [
    t('teamsManagement:table.id'),
    t('teamsManagement:table.name'),
    t('teamsManagement:table.business_vertical'),
    t('teamsManagement:table.can_create_leads_on_own_vertical'),
    '',
    ...(canDelete ? [''] : []),
  ]

  const transformTeams = (teamsToTransform: Team[]): ReactNode[][] => {
    if (!Array.isArray(teamsToTransform)) {
      return []
    }
    return teamsToTransform.map(team => {
      const row: ReactNode[] = [
        team.id,
        team.name ?? '',
        formatBusinessVertical(team, countrySites, t),
        <>
          <IconCarbonCheckmarkOutline
            key='check'
            color={theme.colors.positive600}
            size={32}
            title={t('teamsManagement:table.can_create_leads_on_own_vertical')}
            hidden={!team.can_create_leads_on_own_vertical}
          />
          <IconCarbonMisuseOutline
            key='cross'
            color={theme.colors.negative600}
            size={32}
            title={t('teamsManagement:table.cannot_create_leads_on_own_vertical')}
            hidden={team.can_create_leads_on_own_vertical}
          />
        </>,
        // @ts-expect-error SDButton is a JS component
        <SDButton
          id={'team-' + team.id + '-edit-btn'}
          key={`edit-${team.id}`}
          size={SIZE.compact}
          onClick={() => updateTeam(team)}
        >
          {t('common:edit')}
        </SDButton>,
      ]
      if (canDelete) {
        row.push(
          // @ts-expect-error SDButton is a JS component
          <SDButton
            id={'team-' + team.id + '-delete-btn'}
            key={`delete-${team.id}`}
            size={SIZE.compact}
            onClick={() => {
              if (window.confirm(t('teamsManagement:alert'))) {
                deleteTeam(team.id)
              }
            }}
          >
            {t('common:delete')}
          </SDButton>,
        )
      }
      return row
    })
  }

  if (teamsListIsLoading) {
    return <Card>{t('common:fetching_data')}</Card>
  }

  const errorObject = teamsError as { error?: string } | string | null
  if (errorObject && typeof errorObject === 'object' && 'error' in errorObject && errorObject.error) {
    return (
      <Card>
        <Header1>{errorObject.error ?? t('teamsManagement:error_occurred')}</Header1>
      </Card>
    )
  }

  return (
    <Card>
      <Header1 id='teams-title'>{t('teamsManagement:title')}</Header1>
      {Array.isArray(teams) && teams.length ? (
        <Header2>
          {teams.length} {t('teamsManagement:teams_found')}
        </Header2>
      ) : null}
      <FlexGrid flexGridColumnCount={2} marginBottom='.5rem'>
        <FlexGridItem display='flex' alignItems='center'>
          {/* @ts-expect-error SDButton is a JS component */}
          <SDButton key={0} block={false} size={SIZE.compact} onClick={() => updateTeam(null)}>
            {t('teamsManagement:create_button')}
          </SDButton>
        </FlexGridItem>
      </FlexGrid>
      <Table columns={COLUMNS} data={transformTeams(teams)} />

      <Modal onClose={close} isOpen={isOpen}>
        <ModalHeader>
          {selectedTeam.id === 0
            ? t('teamsManagement:modal.header_create')
            : t('teamsManagement:modal.header_update')}
        </ModalHeader>
        <ModalBody>
          <AddEditModal setIsOpen={setIsOpen} close={close} />
        </ModalBody>
      </Modal>
    </Card>
  )
}

export default TeamsManagement
